import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { handleApiError } from '@/utils/errorHandling'

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia",
  "Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica",
  "Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt",
  "El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon",
  "Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana",
  "Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel",
  "Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Kosovo","Kuwait","Kyrgyzstan",
  "Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar",
  "Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia",
  "Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal",
  "Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman","Pakistan",
  "Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar",
  "Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino",
  "Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia",
  "Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden",
  "Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago",
  "Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay",
  "Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
]

const GRADE_OPTIONS = [
  'Preschool',
  'Primary / Elementary',
  'Middle School',
  'High School',
  'University',
  'Professional Training',
]

const SUBJECT_OPTIONS = [
  'Computer Science',
  'Math',
  'Science',
  'Engineering',
  'Cybersecurity',
  'Digital Literacy',
]

const PP_OPTIONS = ['Yes', 'No', 'Planning to']

export function ProfileOnboarding() {
  const navigate = useNavigate()
  const setUser = useAuthStore((s) => s.setUser)

  const [country, setCountry] = useState('')
  const [countrySearch, setCountrySearch] = useState('')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [institution, setInstitution] = useState('')
  const [grades, setGrades] = useState<string[]>([])
  const [subjects, setSubjects] = useState<string[]>([])
  const [otherSubject, setOtherSubject] = useState('')
  const [includeOther, setIncludeOther] = useState(false)
  const [usingPP, setUsingPP] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const filteredCountries = COUNTRIES.filter((c) =>
    c.toLowerCase().includes(countrySearch.toLowerCase())
  )

  const toggleGrade = (g: string) =>
    setGrades((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]))

  const toggleSubject = (s: string) =>
    setSubjects((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))

  const isValid = country && institution.trim() && grades.length > 0 && subjects.length > 0 && usingPP

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return

    const allSubjects = includeOther && otherSubject.trim()
      ? [...subjects, otherSubject.trim()]
      : subjects

    try {
      setSubmitting(true)
      setError('')
      const response = await apiClient.completeProfileOnboarding({
        country,
        institution: institution.trim(),
        grades,
        subjects: allSubjects,
        using_potato_pirates: usingPP,
      })
      const updatedUser = response.data.user
      setUser(updatedUser)
      localStorage.setItem('cached_user', JSON.stringify(updatedUser))
      navigate('/dashboard', { replace: true })
    } catch (err) {
      handleApiError(err as any, 'complete onboarding')
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-gray-800 border border-gray-700 rounded-2xl p-8">
        <h1 className="text-2xl font-bold text-white mb-1">Complete Your Profile</h1>
        <p className="text-gray-400 text-sm mb-6">
          Tell us a bit about yourself so we can tailor your experience.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Country</label>
            <div className="relative">
              <input
                type="text"
                value={country || countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value)
                  setCountry('')
                  setShowCountryDropdown(true)
                }}
                onFocus={() => setShowCountryDropdown(true)}
                placeholder="Search for your country..."
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm"
              />
              {showCountryDropdown && countrySearch && !country && (
                <div className="absolute z-20 w-full mt-1 bg-gray-900 border border-gray-600 rounded-lg max-h-48 overflow-y-auto">
                  {filteredCountries.length === 0 ? (
                    <div className="px-3 py-2 text-gray-500 text-sm">No results</div>
                  ) : (
                    filteredCountries.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => {
                          setCountry(c)
                          setCountrySearch('')
                          setShowCountryDropdown(false)
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
                      >
                        {c}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Institution */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Institution / Company
            </label>
            <input
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              placeholder="e.g. Singapore University of Technology and Design"
              className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm"
            />
          </div>

          {/* Grades */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Grades you teach
            </label>
            <div className="flex flex-wrap gap-2">
              {GRADE_OPTIONS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGrade(g)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    grades.includes(g)
                      ? 'bg-green-900/50 border-green-500 text-green-300'
                      : 'bg-gray-900 border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* Subjects */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Subjects you teach
            </label>
            <div className="flex flex-wrap gap-2">
              {SUBJECT_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSubject(s)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    subjects.includes(s)
                      ? 'bg-green-900/50 border-green-500 text-green-300'
                      : 'bg-gray-900 border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  {s}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setIncludeOther(!includeOther)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  includeOther
                    ? 'bg-green-900/50 border-green-500 text-green-300'
                    : 'bg-gray-900 border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
              >
                Other
              </button>
            </div>
            {includeOther && (
              <input
                type="text"
                value={otherSubject}
                onChange={(e) => setOtherSubject(e.target.value)}
                placeholder="Specify subject..."
                className="mt-2 w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm"
              />
            )}
          </div>

          {/* Using Potato Pirates */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Are you currently using Potato Pirates products?
            </label>
            <div className="flex gap-3">
              {PP_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setUsingPP(opt)}
                  className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                    usingPP === opt
                      ? 'bg-green-900/50 border-green-500 text-green-300'
                      : 'bg-gray-900 border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={!isValid || submitting}
            className="w-full py-3 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          >
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}
